import { useState } from 'react'
import { ColumnDef } from '@tanstack/react-table'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { DataTable } from '@/components/ui/data-table'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { useAccountOverview, useTransactionRecords, useCreateRecharge, TransactionRecord } from '@/hooks/useAccount'
import { 
  CreditCard, 
  DollarSign, 
  TrendingUp, 
  Calendar,
  RefreshCw,
  Plus,
  Download,
  Receipt,
  Wallet,
  Activity,
  ArrowUpRight,
  ArrowDownRight
} from 'lucide-react'
import { format } from 'date-fns'
import { useToast } from '@/hooks/use-toast'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import * as z from 'zod'

const rechargeSchema = z.object({
  amount: z.number().min(1, '充值金额必须大于0'),
  description: z.string().optional(),
  paymentMethod: z.string().min(1, '请选择支付方式'),
})

type RechargeForm = z.infer<typeof rechargeSchema>

export default function Account() {
  const [activeTab, setActiveTab] = useState('overview')
  const [showRechargeDialog, setShowRechargeDialog] = useState(false)
  const [transactionFilters, setTransactionFilters] = useState({
    transactionType: '',
    startDate: '',
    endDate: '',
  })

  const { toast } = useToast()
  const { data: accountOverview, isLoading: overviewLoading, refetch: refetchOverview } = useAccountOverview()
  const { data: transactionData, isLoading: transactionLoading } = useTransactionRecords({ 
    page: 0, 
    size: 10,
    ...transactionFilters 
  })
  const createRecharge = useCreateRecharge()

  const form = useForm<RechargeForm>({
    resolver: zodResolver(rechargeSchema),
    defaultValues: {
      amount: 0,
      description: '',
      paymentMethod: '',
    },
  })

  const transactionColumns: ColumnDef<TransactionRecord>[] = [
    {
      accessorKey: 'transactionNo',
      header: '交易单号',
      cell: ({ row }) => {
        return <span className="font-mono text-sm">{row.getValue('transactionNo')}</span>
      },
    },
    {
      accessorKey: 'transactionType',
      header: '交易类型',
      cell: ({ row }) => {
        const type = row.getValue('transactionType') as string
        const typeMap = {
          RECHARGE: { label: '充值', variant: 'success' as const, icon: ArrowUpRight },
          CONSUMPTION: { label: '消费', variant: 'destructive' as const, icon: ArrowDownRight },
          REFUND: { label: '退款', variant: 'default' as const, icon: ArrowUpRight },
        }
        const typeInfo = typeMap[type as keyof typeof typeMap] || { label: type, variant: 'secondary' as const, icon: Activity }
        const Icon = typeInfo.icon
        return (
          <div className="flex items-center gap-2">
            <Icon className="h-4 w-4" />
            <Badge variant={typeInfo.variant}>{typeInfo.label}</Badge>
          </div>
        )
      },
    },
    {
      accessorKey: 'amount',
      header: '金额',
      cell: ({ row }) => {
        const amount = row.getValue('amount') as number
        const type = row.getValue('transactionType') as string
        const isPositive = type === 'RECHARGE' || type === 'REFUND'
        return (
          <span className={`font-medium ${isPositive ? 'text-green-600' : 'text-red-600'}`}>
            {isPositive ? '+' : '-'}¥{amount.toFixed(2)}
          </span>
        )
      },
    },
    {
      accessorKey: 'balanceAfter',
      header: '余额',
      cell: ({ row }) => {
        return <span className="font-medium">¥{row.getValue('balanceAfter')}</span>
      },
    },
    {
      accessorKey: 'description',
      header: '描述',
    },
    {
      accessorKey: 'paymentMethod',
      header: '支付方式',
      cell: ({ row }) => {
        const method = row.getValue('paymentMethod') as string
        const methodMap = {
          ALIPAY: '支付宝',
          WECHAT: '微信支付',
          BANK: '银行转账',
          MANUAL: '人工充值',
          SELF: '自助充值',
        }
        return methodMap[method as keyof typeof methodMap] || method
      },
    },
    {
      accessorKey: 'createdAt',
      header: '交易时间',
      cell: ({ row }) => {
        return format(new Date(row.getValue('createdAt')), 'yyyy-MM-dd HH:mm:ss')
      },
    },
  ]

  const handleRecharge = () => {
    setShowRechargeDialog(true)
  }

  const onSubmitRecharge = async (data: RechargeForm) => {
    try {
      // 显示MOCK支付流程
      toast({
        title: '正在跳转支付...',
        description: `正在跳转到${data.paymentMethod === 'ALIPAY' ? '支付宝' : data.paymentMethod === 'WECHAT' ? '微信支付' : '银行转账'}页面`,
      })
      
      // 模拟支付延迟
      await new Promise(resolve => setTimeout(resolve, 2000))
      
      await createRecharge.mutateAsync({
        amount: data.amount,
        description: data.description,
        paymentMethod: data.paymentMethod,
      })
      
      toast({
        title: '充值成功',
        description: `已成功充值 ¥${data.amount}，支付方式：${data.paymentMethod === 'ALIPAY' ? '支付宝' : data.paymentMethod === 'WECHAT' ? '微信支付' : '银行转账'}`,
      })
      setShowRechargeDialog(false)
      form.reset()
      refetchOverview()
    } catch (error) {
      toast({
        title: '充值失败',
        description: error instanceof Error ? error.message : '充值失败，请重试',
        variant: 'destructive',
      })
    }
  }

  const handleExport = () => {
    toast({
      title: '导出成功',
      description: '账单数据已开始导出，请稍后下载',
    })
  }

  const handleFilterChange = (key: string, value: string) => {
    setTransactionFilters(prev => ({ ...prev, [key]: value }))
  }

  return (
    <div className="flex-1 space-y-4 p-8 pt-6">
      <div className="flex items-center justify-between space-y-2">
        <h2 className="text-3xl font-bold tracking-tight">账户管理</h2>
        <div className="flex items-center space-x-2">
          <Button onClick={() => refetchOverview()}>
            <RefreshCw className="mr-2 h-4 w-4" />
            刷新
          </Button>
          <Button onClick={handleRecharge}>
            <Plus className="mr-2 h-4 w-4" />
            账户充值
          </Button>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList>
          <TabsTrigger value="overview">账户概览</TabsTrigger>
          <TabsTrigger value="transactions">交易记录</TabsTrigger>
          <TabsTrigger value="invoice">发票管理</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-4">
          {/* 账户信息卡片 */}
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">账户余额</CardTitle>
                <Wallet className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-green-600">
                  {overviewLoading ? '...' : `¥${(accountOverview?.balance || 0).toFixed(2)}`}
                </div>
                <p className="text-xs text-muted-foreground">
                  可用余额
                </p>
              </CardContent>
            </Card>
            
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">冻结余额</CardTitle>
                <CreditCard className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  {overviewLoading ? '...' : `¥${(accountOverview?.frozenBalance || 0).toFixed(2)}`}
                </div>
                <p className="text-xs text-muted-foreground">
                  冻结金额
                </p>
              </CardContent>
            </Card>
            
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">累计充值</CardTitle>
                <TrendingUp className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-blue-600">
                  {overviewLoading ? '...' : `¥${(accountOverview?.totalRecharge || 0).toFixed(2)}`}
                </div>
                <p className="text-xs text-muted-foreground">
                  历史充值总额
                </p>
              </CardContent>
            </Card>
            
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">累计消费</CardTitle>
                <Activity className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-red-600">
                  {overviewLoading ? '...' : `¥${(accountOverview?.totalConsumption || 0).toFixed(2)}`}
                </div>
                <p className="text-xs text-muted-foreground">
                  历史消费总额
                </p>
              </CardContent>
            </Card>
          </div>

          {/* 使用统计 */}
          <Card>
            <CardHeader>
              <CardTitle>使用统计</CardTitle>
              <CardDescription>
                账户使用情况统计
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid gap-4 md:grid-cols-3">
                <div className="space-y-2">
                  <div className="text-sm font-medium text-muted-foreground">累计发送短信</div>
                  <div className="text-2xl font-bold">
                    {overviewLoading ? '...' : (accountOverview?.totalSmsSent || 0).toString()}
                  </div>
                </div>
                <div className="space-y-2">
                  <div className="text-sm font-medium text-muted-foreground">账户状态</div>
                  <div className="text-2xl font-bold">
                    <Badge variant="outline">{accountOverview?.status || 'ACTIVE'}</Badge>
                  </div>
                </div>
                <div className="space-y-2">
                  <div className="text-sm font-medium text-muted-foreground">最后更新时间</div>
                  <div className="text-sm">
                    {overviewLoading ? '...' : accountOverview?.lastUpdateTime ? 
                      format(new Date(accountOverview.lastUpdateTime), 'yyyy-MM-dd HH:mm:ss') : 
                      '未知'
                    }
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="transactions" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>交易记录</CardTitle>
              <CardDescription>
                查看账户交易明细记录
              </CardDescription>
            </CardHeader>
            <CardContent>
              {/* 筛选器 */}
              <div className="flex gap-4 mb-4">
                <div className="space-y-2">
                  <Label htmlFor="transactionType">交易类型</Label>
                  <Select value={transactionFilters.transactionType} onValueChange={(value) => handleFilterChange('transactionType', value)}>
                    <SelectTrigger className="w-[180px]">
                      <SelectValue placeholder="全部类型" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="">全部类型</SelectItem>
                      <SelectItem value="RECHARGE">充值</SelectItem>
                      <SelectItem value="CONSUMPTION">消费</SelectItem>
                      <SelectItem value="REFUND">退款</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="startDate">开始日期</Label>
                  <Input
                    id="startDate"
                    type="date"
                    value={transactionFilters.startDate}
                    onChange={(e) => handleFilterChange('startDate', e.target.value)}
                    className="w-[180px]"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="endDate">结束日期</Label>
                  <Input
                    id="endDate"
                    type="date"
                    value={transactionFilters.endDate}
                    onChange={(e) => handleFilterChange('endDate', e.target.value)}
                    className="w-[180px]"
                  />
                </div>
              </div>

              {transactionLoading ? (
                <div className="flex items-center justify-center h-32">
                  <div className="text-sm text-muted-foreground">加载中...</div>
                </div>
              ) : (
                <DataTable
                  columns={transactionColumns}
                  data={transactionData?.records || []}
                  searchKey="transactionNo"
                  searchPlaceholder="搜索交易单号..."
                />
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="invoice" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <div className="flex items-center">
                  <Receipt className="mr-2 h-5 w-5" />
                  发票管理
                </div>
                <Button onClick={handleExport}>
                  <Download className="mr-2 h-4 w-4" />
                  导出账单
                </Button>
              </CardTitle>
              <CardDescription>
                管理您的发票申请和开票记录
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="text-center py-8 text-muted-foreground">
                发票管理功能开发中...
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* 充值对话框 */}
      <Dialog open={showRechargeDialog} onOpenChange={setShowRechargeDialog}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>账户充值</DialogTitle>
            <DialogDescription>
              选择充值金额和支付方式，完成充值操作
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={form.handleSubmit(onSubmitRecharge)} className="space-y-6">
            {/* 快速金额选择 */}
            <div className="space-y-3">
              <Label>选择充值金额</Label>
              <div className="grid grid-cols-3 gap-3">
                {[50, 100, 200, 500, 1000, 2000].map((amount) => (
                  <Button
                    key={amount}
                    type="button"
                    variant={form.watch('amount') === amount ? 'default' : 'outline'}
                    className="h-12"
                    onClick={() => form.setValue('amount', amount)}
                  >
                    ¥{amount}
                  </Button>
                ))}
              </div>
            </div>

            {/* 自定义金额 */}
            <div className="space-y-2">
              <Label htmlFor="amount">自定义金额</Label>
              <Input
                id="amount"
                type="number"
                step="0.01"
                min="0.01"
                placeholder="请输入充值金额"
                {...form.register('amount', { valueAsNumber: true })}
              />
              {form.formState.errors.amount && (
                <p className="text-sm text-red-500">{form.formState.errors.amount.message}</p>
              )}
            </div>
            
            {/* 支付方式选择 */}
            <div className="space-y-2">
              <Label htmlFor="paymentMethod">支付方式</Label>
              <Select value={form.watch('paymentMethod')} onValueChange={(value) => form.setValue('paymentMethod', value)}>
                <SelectTrigger>
                  <SelectValue placeholder="请选择支付方式" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="ALIPAY">支付宝</SelectItem>
                  <SelectItem value="WECHAT">微信支付</SelectItem>
                  <SelectItem value="BANK">银行转账</SelectItem>
                </SelectContent>
              </Select>
              {form.formState.errors.paymentMethod && (
                <p className="text-sm text-red-500">{form.formState.errors.paymentMethod.message}</p>
              )}
            </div>
            
            {/* 备注信息 */}
            <div className="space-y-2">
              <Label htmlFor="description">备注信息</Label>
              <Textarea
                id="description"
                placeholder="可选：填写充值备注"
                {...form.register('description')}
              />
            </div>
            
            {/* 余额预览 */}
            <div className="bg-muted p-4 rounded-lg space-y-2">
              <div className="flex justify-between">
                <span className="text-sm text-muted-foreground">当前余额</span>
                <span className="font-medium">¥{accountOverview?.balance?.toFixed(2) || '0.00'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-muted-foreground">充值金额</span>
                <span className="font-medium text-green-600">+¥{(form.watch('amount') || 0).toFixed(2)}</span>
              </div>
              <div className="border-t pt-2 flex justify-between">
                <span className="text-sm font-medium">充值后余额</span>
                <span className="text-lg font-bold text-green-600">
                  ¥{((accountOverview?.balance || 0) + (form.watch('amount') || 0)).toFixed(2)}
                </span>
              </div>
            </div>
            
            {/* 操作按钮 */}
            <div className="flex justify-end space-x-2">
              <Button type="button" variant="outline" onClick={() => setShowRechargeDialog(false)}>
                取消
              </Button>
              <Button type="submit" disabled={createRecharge.isPending}>
                {createRecharge.isPending ? '充值中...' : '确认充值'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  )
}